"use client";

/**
 * MapMock — animated mock of the askFinz Map workspace for /apps/map.
 *
 * Pure presentation: a stylised dotted-grid "map" showing live aircraft dots
 * appearing and moving, vessel markers on a channel strip, and a right-side
 * data console that echoes aircraft state and overlay toggles.
 *
 * NOT a real map (no tile server, no real coordinates). Lives in askfinz.com
 * only. Positions and counts are decorative.
 */
import { useEffect, useRef, useState } from "react";
import {
  Plane,
  Ship,
  Layers,
  ArrowUpRight,
  Sparkles,
  Satellite,
  Cloud,
} from "lucide-react";

type AircraftDot = {
  id: string;
  x: number;
  y: number;
  label: string;
  altitude: string;
  speed: string;
  heading: number;
};

const INITIAL_AIRCRAFT: AircraftDot[] = [
  { id: "a1", x: 22, y: 28, label: "BAW291", altitude: "FL360", speed: "489 kts", heading: 85 },
  { id: "a2", x: 55, y: 18, label: "DLH442", altitude: "FL340", speed: "472 kts", heading: 120 },
  { id: "a3", x: 74, y: 42, label: "EZY77N", altitude: "FL280", speed: "410 kts", heading: 220 },
];

const NEW_AIRCRAFT: AircraftDot = {
  id: "a4", x: 38, y: 58, label: "AFR19", altitude: "FL390", speed: "501 kts", heading: 65,
};

type VesselDot = { id: string; x: number; y: number; label: string };
const VESSELS: VesselDot[] = [
  { id: "v1", x: 18, y: 70, label: "STENA BRITANNICA" },
  { id: "v2", x: 40, y: 75, label: "MSC ELENA" },
  { id: "v3", x: 62, y: 68, label: "P&O PRIDE" },
];

type LayerName = "Aircraft" | "Vessels" | "Satellites" | "Weather";
const LAYER_LIST: LayerName[] = ["Aircraft", "Vessels", "Satellites", "Weather"];

const LAYER_ICONS: Record<LayerName, typeof Plane> = {
  Aircraft: Plane,
  Vessels: Ship,
  Satellites: Satellite,
  Weather: Cloud,
};

export function MapMock() {
  const [aircraft, setAircraft] = useState<AircraftDot[]>(INITIAL_AIRCRAFT);
  const [visibleVessels, setVisibleVessels] = useState(0);
  const [activeLayers, setActiveLayers] = useState<LayerName[]>(["Aircraft"]);
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftDot | null>(INITIAL_AIRCRAFT[0]);
  const [pulseId, setPulseId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setAircraft(INITIAL_AIRCRAFT);
      setVisibleVessels(0);
      setActiveLayers(["Aircraft"]);
      setSelectedAircraft(INITIAL_AIRCRAFT[0]);
      setPulseId(null);

      // 1. New aircraft appears
      schedule(() => {
        setPulseId("a4");
        setAircraft((cur) => [...cur, NEW_AIRCRAFT]);
        setSelectedAircraft(NEW_AIRCRAFT);
      }, 900);
      schedule(() => setPulseId(null), 1700);

      // 2. Switch selection to a2
      schedule(() => setSelectedAircraft(INITIAL_AIRCRAFT[1]), 2800);

      // 3. Toggle Vessels layer on — vessels appear
      schedule(() => {
        setActiveLayers((cur) => [...cur, "Vessels"]);
      }, 3800);
      VESSELS.forEach((_, i) => {
        schedule(() => setVisibleVessels(i + 1), 4100 + i * 400);
      });

      // 4. Toggle Weather on
      schedule(() => {
        setActiveLayers((cur) => [...cur, "Weather"]);
      }, 5800);

      // 5. Cycle back
      schedule(() => {
        setActiveLayers(["Aircraft"]);
        setVisibleVessels(0);
        setSelectedAircraft(INITIAL_AIRCRAFT[0]);
      }, 7200);

      // Loop
      schedule(runCycle, 9000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const showVessels = activeLayers.includes("Vessels");
  const showWeather = activeLayers.includes("Weather");

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            map.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Live
          </span>
        </div>

        {/* Layer toggles */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b hairline bg-foreground/[0.02] flex-wrap">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono mr-1">
            <Layers className="inline h-3 w-3 mr-1" />Layers
          </span>
          {LAYER_LIST.map((l) => {
            const on = activeLayers.includes(l);
            const Icon = LAYER_ICONS[l];
            return (
              <button
                key={l}
                type="button"
                onClick={() =>
                  setActiveLayers((cur) =>
                    on ? cur.filter((x) => x !== l) : [...cur, l]
                  )
                }
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] tracking-[0.14em] uppercase font-mono transition-all " +
                  (on
                    ? "bg-foreground text-background border-foreground"
                    : "text-foreground/65 border-foreground/20 hover:border-foreground/40")
                }
              >
                <Icon className="h-3 w-3" />
                {l}
              </button>
            );
          })}
          <span className="ml-auto text-[10px] font-mono text-foreground/65 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            {aircraft.length} aircraft · {showVessels ? VESSELS.length : 0} vessels
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-px bg-foreground/10">
          {/* Map canvas */}
          <div className="relative bg-background min-h-[360px] overflow-hidden">
            {/* Dotted grid background */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: "radial-gradient(circle, hsl(var(--foreground) / 0.12) 1px, transparent 1px)",
                backgroundSize: "18px 18px",
              }}
            />
            {/* Crosshairs */}
            <div aria-hidden className="absolute top-0 bottom-0 left-1/2 w-px bg-foreground/[0.04]" />
            <div aria-hidden className="absolute left-0 right-0 top-1/2 h-px bg-foreground/[0.04]" />

            {/* Weather overlay — subtle gradient wash */}
            {showWeather && (
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                style={{
                  opacity: showWeather ? 0.35 : 0,
                  background: "radial-gradient(ellipse 70% 50% at 30% 20%, hsl(var(--foreground)/0.08) 0%, transparent 70%)",
                }}
              />
            )}

            {/* Aircraft dots */}
            {aircraft.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelectedAircraft(a)}
                className={
                  "absolute -translate-x-1/2 -translate-y-1/2 group transition-all " +
                  (pulseId === a.id ? "animate-[blur-fade-in_0.5s_ease-out]" : "")
                }
                style={{ left: `${a.x}%`, top: `${a.y}%` }}
                aria-label={a.label}
              >
                <div
                  className={
                    "relative flex items-center justify-center transition-all " +
                    (selectedAircraft?.id === a.id ? "scale-125" : "")
                  }
                  style={{ transform: `rotate(${a.heading}deg)` }}
                >
                  <Plane
                    className={
                      "h-4 w-4 transition-colors " +
                      (selectedAircraft?.id === a.id
                        ? "text-[hsl(var(--accent))] drop-shadow-[0_0_4px_hsl(var(--accent)/0.6)]"
                        : "text-foreground/70")
                    }
                  />
                </div>
                {selectedAircraft?.id === a.id && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 mt-1 top-full whitespace-nowrap px-1.5 py-0.5 rounded bg-foreground text-background text-[9px] font-mono tracking-[0.14em] shadow"
                    style={{ transform: `translateX(-50%) rotate(${-a.heading}deg)` }}
                  >
                    {a.label}
                  </div>
                )}
              </button>
            ))}

            {/* Vessel dots */}
            {showVessels &&
              VESSELS.slice(0, visibleVessels).map((v) => (
                <div
                  key={v.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2 animate-[blur-fade-in_0.5s_ease-out]"
                  style={{ left: `${v.x}%`, top: `${v.y}%` }}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <Ship className="h-3.5 w-3.5 text-foreground/55" />
                    <span className="text-[8px] font-mono text-foreground/55 tracking-[0.1em] bg-background/70 px-1 rounded whitespace-nowrap">
                      {v.label.slice(0, 10)}
                    </span>
                  </div>
                </div>
              ))}

            {/* Legend */}
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-3 rounded-lg border hairline bg-background/85 backdrop-blur px-2.5 py-1.5 text-[10px] font-mono text-foreground/65">
              <span className="inline-flex items-center gap-1.5">
                <Plane className="h-3 w-3" />
                {aircraft.length} tracked
              </span>
              {showVessels && (
                <span className="inline-flex items-center gap-1.5">
                  <Ship className="h-3 w-3" />
                  {visibleVessels} vessels
                </span>
              )}
            </div>
          </div>

          {/* Data console */}
          <div className="bg-background p-4 font-mono text-[11px] text-foreground/75 space-y-3 overflow-hidden">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/65">
              <Plane className="h-3 w-3" />
              {selectedAircraft ? "Selected aircraft" : "Aircraft"}
            </div>

            {selectedAircraft ? (
              <div className="rounded-md bg-foreground/[0.04] px-3 py-2.5 space-y-2 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="text-foreground/90 font-medium">{selectedAircraft.label}</span>
                  <span className="text-[9px] tracking-[0.14em] uppercase bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] px-1.5 py-0.5 rounded font-mono">
                    Airborne
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <div className="text-foreground/50 tracking-[0.14em] uppercase">Altitude</div>
                    <div className="text-foreground/85 mt-0.5">{selectedAircraft.altitude}</div>
                  </div>
                  <div>
                    <div className="text-foreground/50 tracking-[0.14em] uppercase">Speed</div>
                    <div className="text-foreground/85 mt-0.5">{selectedAircraft.speed}</div>
                  </div>
                  <div>
                    <div className="text-foreground/50 tracking-[0.14em] uppercase">Heading</div>
                    <div className="text-foreground/85 mt-0.5">{selectedAircraft.heading}°</div>
                  </div>
                  <div>
                    <div className="text-foreground/50 tracking-[0.14em] uppercase">Updated</div>
                    <div className="text-foreground/85 mt-0.5">just now</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-md bg-foreground/[0.04] px-3 py-2.5 text-foreground/50 text-[10px]">
                Click an aircraft to inspect
              </div>
            )}

            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/65 pt-1">
              <Sparkles className="h-3 w-3" />
              Live counts
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Aircraft", value: aircraft.length, icon: Plane },
                { label: "Vessels", value: showVessels ? visibleVessels : 0, icon: Ship },
                { label: "Satellites", value: activeLayers.includes("Satellites") ? "9,841" : "—", icon: Satellite },
                { label: "Overlays on", value: activeLayers.length, icon: Layers },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-md border hairline bg-foreground/[0.02] px-2.5 py-2">
                  <div className="flex items-center gap-1 text-[9px] tracking-[0.14em] uppercase text-foreground/50">
                    <Icon className="h-2.5 w-2.5" />
                    {label}
                  </div>
                  <div className="mt-1 text-foreground/85 text-[13px] font-medium">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Live at <span className="font-mono text-foreground/65">map.askfinz.ai</span></span>
      </div>
    </div>
  );
}
